<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import {
  useInboxHierarchy,
  hierarchyGroupKey,
} from 'dashboard/composables/useInboxHierarchy';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useExactTimestamp } from 'shared/composables/useExactTimestamp';
import { formatInboxWaitDuration } from 'dashboard/helper/inboxWaitDuration';
import { useAlert } from 'dashboard/composables';
import {
  getInboxIconByType,
  getReadableInboxByType,
  INBOX_TYPES,
} from 'dashboard/helper/inbox';
import NotificationsAPI from 'dashboard/api/notifications';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ConversationHierarchyCard from './ConversationHierarchyCard.vue';

const { t } = useI18n();
const exactTimestamp = useExactTimestamp();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { isAdmin } = useAdmin();
const {
  view,
  groupBy,
  inboxes,
  expandedInboxes,
  expandedGroups,
  cards,
  now,
  isPending,
  error,
  refresh,
  toggleInbox,
  toggleGroup,
  loadMore,
  collapseAll,
} = useInboxHierarchy();
const displayOpen = ref(false);
const groupingOpen = ref(false);
const opening = ref(false);
const inboxById = useMapGetter('inboxes/getInboxById');
const pushFlags = useMapGetter('userNotificationSettings/getSelectedPushFlags');
const emailFlags = useMapGetter(
  'userNotificationSettings/getSelectedEmailFlags'
);
const preferenceFlags = useMapGetter('userNotificationSettings/getUIFlags');
const creationDisabled = computed(
  () =>
    isAdmin.value &&
    pushFlags.value &&
    emailFlags.value &&
    !pushFlags.value.includes('push_conversation_creation') &&
    !emailFlags.value.includes('email_conversation_creation')
);
const views = computed(() =>
  ['unread', 'waiting', 'read'].map(key => ({
    key,
    label: t(`INBOX_HIERARCHY.VIEWS.${key.toUpperCase()}`),
  }))
);
const currentViewLabel = computed(() =>
  t(`INBOX_HIERARCHY.VIEWS.${view.value.toUpperCase()}`)
);
const isAssigneeGrouping = computed(() => groupBy.value === 'assignee');
const groupingOptions = computed(() => [
  { key: 'team', label: t('INBOX_HIERARCHY.BY_INBOX_TEAM') },
  { key: 'assignee', label: t('INBOX_HIERARCHY.BY_INBOX_ASSIGNEE') },
]);
const currentGroupingLabel = computed(
  () => groupingOptions.value.find(option => option.key === groupBy.value).label
);
const groupTitle = group =>
  isAssigneeGrouping.value
    ? group.assignee_name || t('INBOX_HIERARCHY.NO_ASSIGNEE')
    : group.team_name || t('INBOX_HIERARCHY.NO_TEAM');
const waitingSummary = group =>
  t('INBOX_HIERARCHY.GROUP_WAITING', {
    count: group.waiting_count,
    time: formatInboxWaitDuration(group.oldest_waiting_since, now.value, t),
  });
const setGrouping = key => {
  groupBy.value = key;
  groupingOpen.value = false;
};
const isViewSelected = key =>
  view.value === key || (view.value === 'unread_waiting' && key !== 'read');

const originFor = id => {
  const inbox = inboxById.value(id);
  const attributes = inbox.additional_attributes || inbox.additionalAttributes;
  const type = attributes?.gowa?.provisioned
    ? INBOX_TYPES.WHATSAPP
    : inbox.channel_type || inbox.channelType;
  const medium = inbox.medium;
  const readable =
    type === INBOX_TYPES.TWILIO && medium === 'whatsapp'
      ? 'whatsapp'
      : getReadableInboxByType(type, inbox.phone_number || inbox.phoneNumber);
  return {
    icon: getInboxIconByType(type, medium, 'line'),
    label: t(`INBOX_HIERARCHY.ORIGINS.${readable}`),
  };
};

const setView = key => {
  if (key === 'read' || view.value === 'read') {
    view.value = key;
  } else if (view.value === 'unread_waiting') {
    view.value = key === 'unread' ? 'waiting' : 'unread';
  } else if (view.value !== key) {
    view.value = 'unread_waiting';
  }
};
const openConversation = async item => {
  if (opening.value) return;
  opening.value = true;
  try {
    await NotificationsAPI.read('Conversation', item.primary_actor_id);
    store.dispatch('notifications/unReadCount');
    await router.push({
      name: 'inbox_view_conversation',
      params: { inboxId: item.inbox_id, type: 'conversation', id: item.id },
    });
    await refresh();
  } catch {
    useAlert(t('INBOX_HIERARCHY.OPEN_ERROR'));
  } finally {
    opening.value = false;
  }
};
const enableCreation = async () => {
  try {
    await store.dispatch('userNotificationSettings/update', {
      selectedEmailFlags: emailFlags.value,
      selectedPushFlags: [...pushFlags.value, 'push_conversation_creation'],
    });
    useAlert(t('INBOX_HIERARCHY.NOTIFICATIONS_ENABLED'));
  } catch {
    useAlert(t('INBOX_HIERARCHY.PREFERENCE_ERROR'));
  }
};
onMounted(() => store.dispatch('userNotificationSettings/get'));
</script>

<template>
  <div
    class="flex h-full w-full flex-col bg-n-surface-1 lg:min-w-[21.25rem] lg:max-w-[21.25rem] border-e border-n-weak"
    :class="route.params.id ? 'hidden xl:flex' : 'flex'"
    data-inbox-hierarchy
  >
    <div
      class="flex h-[3.25rem] shrink-0 items-center justify-between gap-2 px-3"
    >
      <h1 class="min-w-0 truncate text-heading-2 text-n-slate-12">
        {{ t('INBOX.LIST.TITLE') }}
      </h1>
      <div class="relative">
        <NextButton
          :label="t('INBOX.LIST.DISPLAY_DROPDOWN')"
          icon="i-lucide-chevron-down"
          trailing-icon
          slate
          xs
          faded
          @click="displayOpen = !displayOpen"
        />
        <div
          v-if="displayOpen"
          v-on-clickaway="() => (displayOpen = false)"
          class="absolute end-0 top-full z-50 mt-1 flex w-52 flex-col gap-1 rounded-lg border border-n-weak bg-n-solid-2 p-1 shadow-lg"
        >
          <button
            v-for="option in views"
            :key="option.key"
            type="button"
            class="flex items-center justify-between rounded px-2 py-2 text-start text-sm text-n-slate-12 hover:bg-n-alpha-1"
            :aria-pressed="isViewSelected(option.key)"
            @click="setView(option.key)"
          >
            {{ option.label }}
            <Icon
              v-if="isViewSelected(option.key)"
              icon="i-lucide-check"
              class="size-4 text-n-brand-text"
            />
          </button>
        </div>
      </div>
    </div>
    <div
      class="flex items-center justify-between gap-1 px-3 pb-2 text-xs text-n-slate-11"
    >
      <span class="min-w-0 flex-1 truncate" :title="currentViewLabel">{{
        currentViewLabel
      }}</span>
      <div class="flex shrink-0 items-center gap-1">
        <Spinner v-if="isPending" class="size-3 text-n-brand-text" />
        <div class="relative">
          <NextButton
            :label="currentGroupingLabel"
            :aria-label="t('INBOX_HIERARCHY.GROUP_BY')"
            :aria-expanded="groupingOpen"
            aria-haspopup="menu"
            icon="i-lucide-chevron-down"
            trailing-icon
            slate
            xs
            ghost
            data-hierarchy-grouping
            @click="groupingOpen = !groupingOpen"
          />
          <div
            v-if="groupingOpen"
            v-on-clickaway="() => (groupingOpen = false)"
            role="menu"
            :aria-label="t('INBOX_HIERARCHY.GROUP_BY')"
            class="absolute end-0 top-full z-50 mt-1 flex w-60 flex-col gap-1 rounded-lg border border-n-weak bg-n-solid-2 p-1 shadow-lg"
          >
            <button
              v-for="option in groupingOptions"
              :key="option.key"
              type="button"
              role="menuitemradio"
              :aria-checked="groupBy === option.key"
              class="flex items-center justify-between gap-2 rounded px-2 py-2 text-start text-sm text-n-slate-12 hover:bg-n-alpha-1"
              @click="setGrouping(option.key)"
            >
              {{ option.label }}
              <Icon
                v-if="groupBy === option.key"
                icon="i-lucide-check"
                class="size-4 shrink-0 text-n-brand-text"
              />
            </button>
          </div>
        </div>
        <NextButton
          :aria-label="t('INBOX_HIERARCHY.COLLAPSE_ALL')"
          :title="t('INBOX_HIERARCHY.COLLAPSE_ALL')"
          icon="i-lucide-chevrons-down-up"
          slate
          xs
          ghost
          @click="collapseAll"
        />
      </div>
    </div>
    <div
      v-if="creationDisabled"
      class="mx-3 mb-2 rounded-lg bg-n-amber-2 p-2 text-xs text-n-amber-11"
    >
      <p class="mb-1">{{ t('INBOX_HIERARCHY.NOTIFICATIONS_DISABLED') }}</p>
      <NextButton
        :label="t('INBOX_HIERARCHY.ENABLE_NOTIFICATIONS')"
        :is-loading="preferenceFlags.isUpdating"
        xs
        ghost
        @click="enableCreation"
      />
    </div>
    <div v-if="error" role="alert" class="mx-3 mb-2 text-xs text-n-ruby-11">
      {{ t('INBOX_HIERARCHY.FETCH_ERROR') }}
      <NextButton
        :label="t('INBOX_HIERARCHY.RETRY')"
        xs
        ghost
        @click="refresh"
      />
    </div>
    <div class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 pb-3">
      <p
        v-if="!inboxes.length && !isPending && !error"
        class="p-4 text-center text-sm text-n-slate-11"
      >
        {{ t(`INBOX_HIERARCHY.EMPTY.${view.toUpperCase()}`) }}
      </p>
      <div
        v-for="inbox in inboxes"
        :key="inbox.id"
        :data-hierarchy-inbox="inbox.id"
        class="border-b border-n-weak py-1"
      >
        <button
          type="button"
          class="flex w-full min-w-0 items-center gap-2 rounded-lg px-2 py-2 text-start text-sm text-n-slate-12 hover:bg-n-alpha-1"
          :aria-expanded="!!expandedInboxes[inbox.id]"
          :aria-label="inbox.name"
          @click="toggleInbox(inbox.id)"
        >
          <Icon
            :icon="
              expandedInboxes[inbox.id]
                ? 'i-lucide-chevron-down'
                : 'i-lucide-chevron-right'
            "
            class="size-3 shrink-0 text-n-slate-10"
          />
          <Icon
            :icon="originFor(inbox.id).icon"
            class="size-3.5 shrink-0 text-n-slate-11"
          />
          <span class="min-w-0 flex-1">
            <span class="block truncate">{{ inbox.name }}</span>
            <span
              v-if="inbox.waiting_count"
              class="block whitespace-normal text-xs text-n-amber-11"
              :title="
                t('INBOX_HIERARCHY.WAITING_SINCE', {
                  time: exactTimestamp(inbox.oldest_waiting_since),
                })
              "
              data-inbox-wait-summary
              >{{ waitingSummary(inbox) }}</span
            >
          </span>
          <span
            class="rounded bg-n-teal-3 px-1.5 text-xs text-n-teal-11"
            data-inbox-count
            >{{ inbox.count }}</span
          >
        </button>
        <div
          v-if="expandedInboxes[inbox.id]"
          class="ms-3 border-s border-n-weak ps-2"
        >
          <div
            v-for="group in inbox.groups"
            :key="hierarchyGroupKey(group)"
            :data-hierarchy-team="
              !isAssigneeGrouping ? hierarchyGroupKey(group) : undefined
            "
            :data-hierarchy-assignee="
              isAssigneeGrouping ? (group.assignee_id ?? 'none') : undefined
            "
          >
            <button
              type="button"
              class="flex w-full min-w-0 items-center gap-2 rounded-lg px-2 py-2 text-start text-xs text-n-slate-11 hover:bg-n-alpha-1"
              :aria-expanded="!!expandedGroups[hierarchyGroupKey(group)]"
              :aria-label="groupTitle(group)"
              :title="group.assignee_email || groupTitle(group)"
              @click="toggleGroup(group)"
            >
              <Icon
                :icon="
                  expandedGroups[hierarchyGroupKey(group)]
                    ? 'i-lucide-chevron-down'
                    : 'i-lucide-chevron-right'
                "
                class="size-3 shrink-0 text-n-slate-10"
              />
              <Avatar
                v-if="isAssigneeGrouping && group.assignee_id"
                :name="group.assignee_name"
                :size="20"
                rounded-full
                class="shrink-0"
              />
              <Icon
                v-else
                :icon="
                  isAssigneeGrouping
                    ? 'i-lucide-user-round'
                    : 'i-lucide-users-round'
                "
                class="size-3 shrink-0"
              />
              <span class="min-w-0 flex-1">
                <span class="block truncate">{{ groupTitle(group) }}</span>
                <span
                  v-if="group.waiting_count"
                  class="block whitespace-normal text-xs text-n-amber-11"
                  :title="
                    t('INBOX_HIERARCHY.WAITING_SINCE', {
                      time: exactTimestamp(group.oldest_waiting_since),
                    })
                  "
                  data-group-wait-summary
                  >{{ waitingSummary(group) }}</span
                >
              </span>
              <span
                class="rounded bg-n-alpha-1 px-1.5 text-xs text-n-slate-11"
                data-group-count
                :data-team-count="!isAssigneeGrouping ? '' : undefined"
                :data-assignee-count="isAssigneeGrouping ? '' : undefined"
                >{{ group.count }}</span
              >
            </button>
            <div
              v-if="expandedGroups[hierarchyGroupKey(group)]"
              class="ms-3 border-s border-n-weak ps-2"
            >
              <ConversationHierarchyCard
                v-for="item in cards[hierarchyGroupKey(group)] || []"
                :key="item.id"
                :item="item"
                :origin="originFor(inbox.id)"
                :now="now"
                :active="Number(route.params.id) === item.id"
                @open="openConversation"
              />
              <NextButton
                v-if="
                  (cards[hierarchyGroupKey(group)]?.length || 0) < group.count
                "
                :label="t('INBOX_HIERARCHY.LOAD_MORE')"
                :disabled="isPending"
                :is-loading="isPending"
                xs
                ghost
                class="my-1"
                @click="loadMore(group)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
